import {useNavigate} from 'react-router-dom'
import {ContentArea} from '@/components/shared/ContentArea'
import {ExpandableRow} from '@/components/shared/ExpandableRow'
import {InlineRow} from '@/components/shared/InlineRow'
import {CheckCircle, Clock, AlertTriangle, AlertCircle} from 'lucide-react'
import {Badge} from '@/components/ui/badge'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {ErrorBanner} from '@/components/ui/error-banner'
import {useI18n} from '@/i18n'
import {Icon} from '@/components/shared/Icon'
import {formatDateOnly} from '@/lib/date-utils'
import {routes} from '@/lib/routes'
import {PageSpinner} from '@/components/shared/PageSpinner'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {Strong} from '@/components/shared/Strong'
import {SmallText} from '@/components/shared/SmallText'
import {Stack} from '@/components/shared/Stack'
import {Button} from '@/components/ui/button'
import {useDashboardNotifications} from './useNotifications'
import {EventCategoryValues, getEventCategory, getEventTypeBadgeVariant, getEventTypeLabel} from './notificationUtils'

function getStatusIcon(eventType: string) {
    const category = getEventCategory(eventType)
    switch (category) {
        case EventCategoryValues.SUCCESS:
            return <Icon icon={CheckCircle} size="lg" className="text-status-success"/>
        case EventCategoryValues.ERROR:
            return <Icon icon={AlertCircle} size="lg" className="text-destructive"/>
        case EventCategoryValues.WARNING:
            return <Icon icon={AlertTriangle} size="lg" className="text-status-warning"/>
        case EventCategoryValues.INFO:
            return <Icon icon={Clock} size="lg" className="text-primary"/>
    }
}

export function RecentNotifications() {
    const i18n = useI18n()
    const navigate = useNavigate()
    const {data, isLoading, error} = useDashboardNotifications()

    const notifications = data?.content ?? []

    return (
        <Card data-testid="recent-notifications-card">
            <CardHeader>
                <CardTitle className="text-base" data-testid="card-title-recent-notifications">{i18n.myDashboard.recentNotifications}</CardTitle>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <PageSpinner className="py-8"/>
                ) : error ? (
                    <ErrorBanner error={error}/>
                ) : notifications.length === 0 ? (
                    <InlineEmptyText>{i18n.myDashboard.recentNotificationsEmpty}</InlineEmptyText>
                ) : (
                    <Stack bleed divideY>
                        {notifications.map((notification) => (
                            <ExpandableRow
                                key={notification.id}
                                onClick={() => navigate(routes.my.notifications.detail(notification.id))}
                                data-testid={`dashboard-notification-${notification.id}`}
                                className="px-6 py-3"
                            >
                                <InlineRow gap="lg" className="w-full">
                                    {getStatusIcon(notification.eventType)}
                                    <ContentArea>
                                        <Strong truncate>{notification.title}</Strong>
                                    </ContentArea>
                                    <Badge variant={getEventTypeBadgeVariant(notification.eventType)} className="shrink-0">
                                        {getEventTypeLabel(notification.eventType, i18n.notifications)}
                                    </Badge>
                                    <SmallText className={`whitespace-nowrap`}>
                                        {formatDateOnly(notification.createdAt)}
                                    </SmallText>
                                </InlineRow>
                            </ExpandableRow>
                        ))}
                    </Stack>
                )}
                <Stack topBorder>
                    <Button
                        variant="link"
                        className="px-0 justify-start"
                        onClick={() => navigate(routes.my.notifications.path())}
                        data-testid="dashboard-view-all-notifications"
                    >
                        {i18n.myDashboard.viewAllNotifications}
                        {' →'}
                    </Button>
                </Stack>
            </CardContent>
        </Card>
    )
}
